import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { OfferService } from 'src/app/service/offer.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { environment } from 'src/environments/environment';
import { DeleteDialogBoxComponent } from '../delete-dialog-box/delete-dialog-box.component';

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss'],
})
export class OfferComponent implements OnInit {
  displayedColumns: string[] = [
    'order',
    'image',
    'name',
    'type',
    'duration',
    'totalQty',
    'targets',
    'status',
    'action',
  ];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  environmentURL = environment.apiUrl;
  spinner: boolean = false;
  offers: any[] = [];

  // Filter Form Controls
  searchControl = new FormControl('');
  startDateControl = new FormControl<Date | null>(null);
  endDateControl = new FormControl<Date | null>(null);
  statusControl = new FormControl('all');
  typeControl = new FormControl('all');

  // Summary Metrics
  totalOffersCount = 0;
  activeOffersCount = 0;
  luckyDrawCount = 0;
  milestoneCount = 0;

  constructor(
    private offerService: OfferService,
    private router: Router,
    private titleService: Title,
    private toastr: ToastrNotificationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Offer List');
    this.loadOffers();
  }

  loadOffers(filters?: any): void {
    this.spinner = true;
    this.offerService.getAllOffers(filters).subscribe({
      next: (response) => {
        this.spinner = false;
        if (response && response.success) {
          this.offers = response.offers || [];
        } else {
          this.offers = [];
        }
        this.dataSource = new MatTableDataSource(this.offers);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.calculateMetrics(this.offers);
      },
      error: (error) => {
        this.spinner = false;
        this.toastr.error(
          error.error?.error || error.message || 'Could not load offers',
          'Error'
        );
      },
    });
  }

  calculateMetrics(offersList: any[]): void {
    this.totalOffersCount = offersList.length;
    this.activeOffersCount = offersList.filter((o) => o.isActive).length;
    this.luckyDrawCount = offersList.filter((o) => o.isLuckyDraw).length;
    this.milestoneCount = offersList.filter((o) => !o.isLuckyDraw).length;
  }

  applyFilter(): void {
    const filters: any = {};

    const searchVal = this.searchControl.value?.trim();
    if (searchVal) {
      filters.search = searchVal;
    }

    if (this.startDateControl.value) {
      filters.startDate = this.formatDate(this.startDateControl.value);
    }

    if (this.endDateControl.value) {
      filters.endDate = this.formatDate(this.endDateControl.value);
    }

    if (this.statusControl.value && this.statusControl.value !== 'all') {
      filters.status = this.statusControl.value;
    }

    if (this.typeControl.value === 'luckyDraw') {
      filters.isLuckyDraw = 'true';
    } else if (this.typeControl.value === 'milestone') {
      filters.isLuckyDraw = 'false';
    }

    this.loadOffers(filters);
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.startDateControl.setValue(null);
    this.endDateControl.setValue(null);
    this.statusControl.setValue('all');
    this.typeControl.setValue('all');
    this.loadOffers();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.searchControl.value?.trim() ||
      this.startDateControl.value ||
      this.endDateControl.value ||
      (this.statusControl.value && this.statusControl.value !== 'all') ||
      (this.typeControl.value && this.typeControl.value !== 'all')
    );
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  addOffer(): void {
    this.router.navigate(['/addOffer']);
  }

  onEdit(id: string): void {
    this.router.navigate(['/editOffer', id]);
  }

  onDelete(id: string): void {
    const dialogRef = this.dialog.open(DeleteDialogBoxComponent);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.offerService.deleteOffer(id).subscribe({
          next: () => {
            this.toastr.success('Offer deleted successfully', 'Success');
            this.loadOffers();
          },
          error: (error) => {
            this.toastr.error(
              error.error?.error || error.message || 'Could not delete offer',
              'Error'
            );
          },
        });
      }
    });
  }

  onToggleStatus(element: any): void {
    const newStatus = !element.isActive;
    this.offerService.toggleOfferStatus(element._id, newStatus).subscribe({
      next: (response) => {
        element.isActive = newStatus;
        this.calculateMetrics(this.offers);
        this.toastr.success(
          `Offer ${newStatus ? 'activated' : 'deactivated'} successfully`,
          'Status Updated'
        );
      },
      error: (error) => {
        this.toastr.error(
          error.error?.error || error.message || 'Could not update status',
          'Error'
        );
      },
    });
  }

  getOfferStatusLabel(offer: any): { label: string; class: string } {
    if (!offer.isActive) {
      return { label: 'Inactive', class: 'badge-inactive' };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(offer.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(offer.endDate);
    end.setHours(23, 59, 59, 999);

    if (today < start) {
      return { label: 'Upcoming', class: 'badge-upcoming' };
    } else if (today > end) {
      return { label: 'Expired', class: 'badge-expired' };
    } else {
      return { label: 'Active', class: 'badge-active' };
    }
  }
}
